import React, { useEffect, useState } from 'react';
import { businessToday, errorText, ORDER_TYPE_LABEL, OrderType, PosDayStatus, posApi, PosSalesReport, rupees } from './api';

const daysAgo = (n: number) => {
  const d = new Date(`${businessToday()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};

function SalesView({ r }: { r: PosSalesReport }) {
  return (
    <>
      <div className="pos-stats">
        <div className="pos-stat"><small>Net sales</small><b>{rupees(r.net)}</b></div>
        <div className="pos-stat"><small>Orders</small><b>{r.orders}</b></div>
        <div className="pos-stat"><small>Guests</small><b>{r.covers}</b></div>
        <div className="pos-stat"><small>Average bill</small><b>{rupees(r.averageBill)}</b></div>
        <div className="pos-stat"><small>Discounts</small><b>{rupees(r.discounts)}</b></div>
        <div className="pos-stat"><small>Tax collected</small><b>{rupees(r.tax)}</b></div>
      </div>
      <div className="pos-grid2">
        <div className="pos-panel">
          <h3 className="pos-h">Payments</h3>
          <table className="pos-table-list">
            <thead><tr><th>Method</th><th className="num">Count</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {r.payments.length === 0 && <tr><td colSpan={3} className="pos-muted">No payments.</td></tr>}
              {r.payments.map((p) => <tr key={p.method}><td>{p.method}</td><td className="num">{p.count}</td><td className="num">{rupees(p.amount)}</td></tr>)}
            </tbody>
          </table>
          <h3 className="pos-h" style={{ marginTop: 16 }}>Sales summary</h3>
          <table className="pos-table-list">
            <tbody>
              <tr><td>Gross (menu value)</td><td className="num">{rupees(r.gross)}</td></tr>
              <tr><td>Discounts</td><td className="num">−{rupees(r.discounts)}</td></tr>
              <tr><td>Service charge</td><td className="num">{rupees(r.serviceCharge)}</td></tr>
              <tr><td>Tax</td><td className="num">{rupees(r.tax)}</td></tr>
              <tr><td>Round off</td><td className="num">{rupees(r.roundOff)}</td></tr>
              <tr><td><b>Net</b></td><td className="num"><b>{rupees(r.net)}</b></td></tr>
              {r.byType.map((t) => <tr key={t.orderType}><td>{ORDER_TYPE_LABEL[t.orderType as OrderType] ?? t.orderType} ({t.orders})</td><td className="num">{rupees(t.net)}</td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="pos-panel">
          <h3 className="pos-h">Tax summary (for GST/VAT returns)</h3>
          <table className="pos-table-list">
            <thead><tr><th>Tax</th><th className="num">Taxable</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {r.taxes.length === 0 && <tr><td colSpan={3} className="pos-muted">No tax.</td></tr>}
              {r.taxes.map((t) => <tr key={t.label}><td>{t.label}</td><td className="num">{rupees(t.taxable)}</td><td className="num">{rupees(t.amount)}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pos-panel" style={{ marginTop: 14 }}>
        <h3 className="pos-h">Item-wise sales</h3>
        <table className="pos-table-list">
          <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {r.items.length === 0 && <tr><td colSpan={3} className="pos-muted">No items sold.</td></tr>}
            {r.items.map((i) => <tr key={`${i.name}|${i.variantName ?? ''}`}><td>{i.name}{i.variantName ? ` (${i.variantName})` : ''}</td><td className="num">{i.quantity}</td><td className="num">{rupees(i.amount)}</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="pos-grid2" style={{ marginTop: 14 }}>
        <div className="pos-panel">
          <h3 className="pos-h">Voids ({r.voids.length})</h3>
          <table className="pos-table-list">
            <thead><tr><th>Order</th><th>Item</th><th>Reason / by</th><th className="num">Value</th></tr></thead>
            <tbody>
              {r.voids.length === 0 && <tr><td colSpan={4} className="pos-muted">None.</td></tr>}
              {r.voids.map((v, i) => <tr key={i}><td>#{v.orderNo}</td><td>{v.quantity} × {v.item}</td><td>{v.reason} · {v.by}</td><td className="num">{rupees(v.amount)}</td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="pos-panel">
          <h3 className="pos-h">Cancelled orders ({r.cancellations.length})</h3>
          <table className="pos-table-list">
            <thead><tr><th>Order</th><th>Reason / by</th><th className="num">Value</th></tr></thead>
            <tbody>
              {r.cancellations.length === 0 && <tr><td colSpan={3} className="pos-muted">None.</td></tr>}
              {r.cancellations.map((c) => <tr key={c.orderNo}><td>#{c.orderNo}</td><td>{c.reason} · {c.by}</td><td className="num">{rupees(c.value)}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// Sales over a date range (business days). Managers and owners only.
export const ReportsPage: React.FC = () => {
  const [from, setFrom] = useState(businessToday());
  const [to, setTo] = useState(businessToday());
  const [report, setReport] = useState<PosSalesReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setReport(null);
    setError(null);
    posApi.sales(from, to).then(setReport).catch((err) => setError(errorText(err)));
  }, [from, to]);
  const preset = (a: string, b: string) => { setFrom(a); setTo(b); };
  return (
    <>
      <div className="pos-floor-head">
        <h2 className="pos-h" style={{ margin: 0 }}>Reports</h2>
        <button className="pos-btn sm" onClick={() => preset(businessToday(), businessToday())}>Today</button>
        <button className="pos-btn sm" onClick={() => preset(daysAgo(1), daysAgo(1))}>Yesterday</button>
        <button className="pos-btn sm" onClick={() => preset(daysAgo(6), businessToday())}>Last 7 days</button>
        <button className="pos-btn sm" onClick={() => preset(`${businessToday().slice(0, 8)}01`, businessToday())}>This month</button>
        <input type="date" className="pos-input" style={{ width: 170 }} value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <input type="date" className="pos-input" style={{ width: 170 }} value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="To" />
        <button className="pos-btn sm" onClick={() => window.print()}>Print</button>
      </div>
      {error && <div className="pos-msg err">{error}</div>}
      {report ? <SalesView r={report} /> : !error && <p className="pos-muted">Loading...</p>}
    </>
  );
};

// End of day: check there are no open orders, count the cash drawer, close the day.
export const DayClosePage: React.FC = () => {
  const [date, setDate] = useState(businessToday());
  const [status, setStatus] = useState<PosDayStatus | null>(null);
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = (d: string) => {
    setStatus(null);
    setError(null);
    posApi.day(d).then(setStatus).catch((err) => setError(errorText(err)));
  };
  useEffect(() => load(date), [date]);

  const close = async () => {
    if (!status) return;
    setBusy(true);
    setError(null);
    try {
      await posApi.closeDay(status.businessDate, Number(counted) || 0, note || undefined);
      load(date);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const diff = (Number(counted) || 0) - (status?.cashExpected ?? 0);
  return (
    <>
      <div className="pos-floor-head">
        <h2 className="pos-h" style={{ margin: 0 }}>Day close</h2>
        <input type="date" className="pos-input" style={{ width: 180 }} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Business date" />
      </div>
      {error && <div className="pos-msg err">{error}</div>}
      {!status ? !error && <p className="pos-muted">Loading...</p> : (
        <>
          {status.closed ? (
            <div className="pos-panel" style={{ marginBottom: 14 }}>
              <div className="pos-msg ok">Closed by {status.closed.closedBy} at {new Date(status.closed.closedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}.</div>
              <table className="pos-table-list"><tbody>
                <tr><td>Cash expected</td><td className="num">{rupees(status.closed.cashExpected)}</td></tr>
                <tr><td>Cash counted</td><td className="num">{rupees(status.closed.cashCounted)}</td></tr>
                <tr><td>Difference</td><td className="num" style={{ color: status.closed.difference === 0 ? undefined : '#ff8a8a' }}>{rupees(status.closed.difference)}</td></tr>
                {status.closed.note && <tr><td>Note</td><td>{status.closed.note}</td></tr>}
              </tbody></table>
            </div>
          ) : (
            <div className="pos-panel" style={{ marginBottom: 14, maxWidth: 560 }}>
              {status.openOrders > 0 && <div className="pos-msg err">{status.openOrders} order(s) are still open. Settle or cancel them before closing the day.</div>}
              <table className="pos-table-list" style={{ marginBottom: 12 }}><tbody>
                <tr><td>Cash expected in drawer</td><td className="num"><b>{rupees(status.cashExpected)}</b></td></tr>
              </tbody></table>
              <div className="pos-row">
                <label className="pos-field">Cash counted (₹)<input className="pos-input" inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)} /></label>
                <label className="pos-field">Note (optional)<input className="pos-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} /></label>
              </div>
              {counted !== '' && <p className={diff === 0 ? 'pos-muted' : ''} style={{ color: diff === 0 ? undefined : '#ff8a8a' }}>Difference: {rupees(diff)}{diff < 0 ? ' (short)' : diff > 0 ? ' (excess)' : ''}</p>}
              <button className="pos-btn gold" disabled={busy || status.openOrders > 0 || counted === ''} onClick={close}>{busy ? 'Closing...' : 'Close day'}</button>
            </div>
          )}
          <SalesView r={status.report} />
        </>
      )}
    </>
  );
};
